/*
  ЦИФРОВОЙ ДИАК ЗФ — RICH-TEXT EDITOR & VIEW
  Лёгкая обёртка над TipTap для описаний задач и комментариев.

  Зачем:
  - При вставке (paste) из Word/Google Docs/Notion/веба сохраняется
    форматирование (жирный/курсив/списки/заголовки) — а не превращается
    в plain-text, как в обычном <textarea>.
  - Тулбар над полем даёт минимально нужный набор: жирный, курсив,
    подчёркивание, зачёркивание, размер (заголовки H1/H2/H3 + обычный),
    маркированный список, нумерованный список, выделение (highlight),
    «Очистить форматирование».
  - Шрифт не выбирается — наследуется со страницы. По UX-замыслу.

  Хранение:
  - Поле (description / comment.body) остаётся string.
  - Новые значения — HTML.
  - Старые plain-text значения детектируются эвристикой `looksLikeHtml`
    и рендерятся как обычный текст с переносами строк, чтобы ничего
    не сломалось у уже сохранённых карточек/комментариев.
*/

import React, { useEffect, useMemo, useRef } from "react";
import { useEditor, EditorContent, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Highlight from "@tiptap/extension-highlight";
import Placeholder from "@tiptap/extension-placeholder";
import {
  TextStyle,
  FontSize,
  Color,
  BackgroundColor,
} from "@tiptap/extension-text-style";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  List,
  ListOrdered,
  Highlighter,
  RemoveFormatting,
  Palette,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";

// ─────────────────────────────────────────────────────────────────────────────
// helpers

/**
 * Эвристика: похоже ли значение на HTML, который мы сами сгенерировали.
 * Если в строке встречаются типичные блочные/инлайн-теги — считаем HTML.
 * Иначе трактуем как plain-text (старые сохранённые значения).
 */
export function looksLikeHtml(s: string): boolean {
  if (!s) return false;
  return /<\/?(p|div|br|ul|ol|li|h[1-6]|strong|em|u|s|mark|span|a|img|blockquote|code|pre)\b/i.test(
    s,
  );
}

/** Подстраховка: если editor пустой, отдаём пустую строку, а не "<p></p>". */
function htmlOrEmpty(editor: Editor): string {
  if (editor.isEmpty) return "";
  return editor.getHTML();
}

// ─────────────────────────────────────────────────────────────────────────────
// Toolbar

interface ToolbarBtnProps {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}

function ToolbarBtn({ onClick, active, disabled, title, children }: ToolbarBtnProps) {
  return (
    <button
      type="button"
      onMouseDown={(e) => {
        // Не сбрасываем выделение в редакторе при клике по кнопке.
        e.preventDefault();
      }}
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      aria-pressed={active}
      className={cn(
        "inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors",
        "hover:bg-muted hover:text-foreground",
        "disabled:pointer-events-none disabled:opacity-40",
        active && "bg-foreground/10 text-foreground",
      )}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <span className="mx-0.5 h-5 w-px bg-border/70" aria-hidden />;
}

interface RichTextToolbarProps {
  editor: Editor;
}

/**
 * Размер шрифта — ИНЛАЙН-форматирование. Применяется только к выделенному
 * фрагменту (как Bold/Italic), а не ко всему абзацу. Пустое значение —
 * сбросить размер к дефолтному (наследовать со страницы).
 */
const FONT_SIZE_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Обычный" },
  { value: "11px", label: "Мелкий" },
  { value: "16px", label: "Средний" },
  { value: "18px", label: "Крупный" },
  { value: "22px", label: "Очень крупный" },
];

/**
 * Цвет выделенного текста. Native `<input type="color">` обёрнут в
 * лейбл-«кнопку», чтобы вписаться в стиль тулбара. Кнопка слева сбрасывает
 * цвет к дефолтному (наследуется со страницы).
 */
function ColorPicker({ editor }: { editor: Editor }) {
  const active = editor.getAttributes("textStyle")?.color as
    | string
    | undefined;
  const hasColor = !!active && active !== "null";
  const swatch = hasColor ? active : "#0055A5";

  return (
    <span className="inline-flex h-7 items-stretch overflow-hidden rounded-md border border-border/70 bg-background">
      <label
        title={`Цвет выделенного текста${hasColor ? ` (сейчас ${active})` : ""}`}
        aria-label="Цвет текста"
        className="relative inline-flex cursor-pointer items-center gap-1 px-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        onMouseDown={(e) => e.preventDefault()}
      >
        <Palette className="h-3.5 w-3.5" strokeWidth={2} />
        <span
          aria-hidden
          className="h-3 w-3 rounded-sm border border-border/50"
          style={{ backgroundColor: swatch }}
        />
        <input
          type="color"
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          value={hasColor && /^#[0-9a-f]{6}$/i.test(active!) ? active! : "#0055A5"}
          onChange={(e) =>
            editor.chain().focus().setColor(e.target.value).run()
          }
        />
      </label>
      {hasColor ? (
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => editor.chain().focus().unsetColor().run()}
          className="inline-flex items-center justify-center border-l border-border/70 px-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-destructive"
          title="Сбросить цвет (по умолчанию)"
          aria-label="Сбросить цвет"
        >
          <X className="h-3 w-3" strokeWidth={2.5} />
        </button>
      ) : null}
    </span>
  );
}

function FontSizeSelect({ editor }: { editor: Editor }) {
  // Берём активный fontSize у выделения. Если ни одного — пусто.
  const active = editor.getAttributes("textStyle")?.fontSize as
    | string
    | undefined;
  const current = FONT_SIZE_OPTIONS.find((o) => o.value === active)
    ? active ?? ""
    : "";

  return (
    <select
      value={current}
      onChange={(e) => {
        const v = e.target.value;
        if (!v) {
          editor.chain().focus().unsetFontSize().run();
        } else {
          editor.chain().focus().setFontSize(v).run();
        }
      }}
      className="h-7 cursor-pointer rounded-md border border-border/70 bg-background px-1.5 text-[11px] font-medium text-foreground transition-colors hover:border-foreground/30 focus:outline-none focus:ring-2 focus:ring-[var(--nn-blue)]/20"
      title="Размер выделенного текста"
      aria-label="Размер текста"
    >
      {FONT_SIZE_OPTIONS.map((o) => (
        <option key={o.value || "default"} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function RichTextToolbar({ editor }: RichTextToolbarProps) {
  return (
    <div
      className={cn(
        // sticky top-0 — чтобы тулбар «прилипал» к верху при скролле
        // содержимого карточки. Чуть полупрозрачный фон с blur, чтобы
        // текст под ним проступал, но кнопки оставались читаемыми.
        "sticky top-0 z-10 flex flex-wrap items-center gap-0.5 rounded-t-md border-b border-border/70 bg-muted/90 px-1.5 py-1 backdrop-blur supports-[backdrop-filter]:bg-muted/75",
      )}
      role="toolbar"
      aria-label="Форматирование текста"
    >
      <FontSizeSelect editor={editor} />

      <Divider />

      <ColorPicker editor={editor} />

      <Divider />

      <ToolbarBtn
        title="Жирный (Cmd/Ctrl + B)"
        active={editor.isActive("bold")}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold className="h-3.5 w-3.5" strokeWidth={2.5} />
      </ToolbarBtn>
      <ToolbarBtn
        title="Курсив (Cmd/Ctrl + I)"
        active={editor.isActive("italic")}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic className="h-3.5 w-3.5" strokeWidth={2} />
      </ToolbarBtn>
      <ToolbarBtn
        title="Подчёркнутый (Cmd/Ctrl + U)"
        active={editor.isActive("underline")}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <UnderlineIcon className="h-3.5 w-3.5" strokeWidth={2} />
      </ToolbarBtn>
      <ToolbarBtn
        title="Зачёркнутый"
        active={editor.isActive("strike")}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      >
        <Strikethrough className="h-3.5 w-3.5" strokeWidth={2} />
      </ToolbarBtn>
      <ToolbarBtn
        title="Выделить (маркер)"
        active={editor.isActive("highlight")}
        onClick={() => editor.chain().focus().toggleHighlight().run()}
      >
        <Highlighter className="h-3.5 w-3.5" strokeWidth={2} />
      </ToolbarBtn>

      <Divider />

      <ToolbarBtn
        title="Маркированный список"
        active={editor.isActive("bulletList")}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <List className="h-3.5 w-3.5" strokeWidth={2} />
      </ToolbarBtn>
      <ToolbarBtn
        title="Нумерованный список"
        active={editor.isActive("orderedList")}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered className="h-3.5 w-3.5" strokeWidth={2} />
      </ToolbarBtn>

      <Divider />

      <ToolbarBtn
        title="Очистить форматирование"
        onClick={() =>
          editor
            .chain()
            .focus()
            .unsetAllMarks()
            .clearNodes()
            .run()
        }
      >
        <RemoveFormatting className="h-3.5 w-3.5" strokeWidth={2} />
      </ToolbarBtn>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Editor

export interface RichTextEditorProps {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  minHeight?: string;
  /** Если true — вернуть фокус в редактор сразу после монтирования. */
  autoFocus?: boolean;
  className?: string;
  readOnly?: boolean;
  /** Вызывается при Cmd/Ctrl+Enter (полезно для отправки комментария). */
  onSubmit?: () => void;
  /** Дополнительные обработчики paste/drop — пробрасываются в DOM редактора. */
  onPasteCapture?: React.ClipboardEventHandler<HTMLDivElement>;
  onDropCapture?: React.DragEventHandler<HTMLDivElement>;
}

/**
 * Универсальный rich-text редактор. Если `value` пришёл как plain-text
 * (без HTML-тегов), при первом монтировании оборачиваем в <p>, чтобы
 * пользователь мог продолжать писать в едином формате.
 */
export function RichTextEditor({
  value,
  onChange,
  placeholder,
  minHeight = "6rem",
  autoFocus = false,
  className,
  readOnly = false,
  onSubmit,
  onPasteCapture,
  onDropCapture,
}: RichTextEditorProps) {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onSubmitRef = useRef(onSubmit);
  onSubmitRef.current = onSubmit;

  const initialContent = useMemo(() => {
    if (!value) return "";
    if (looksLikeHtml(value)) return value;
    // Plain-text → пара <p>…</p>, переводы строк в <br/> в каждом параграфе.
    const escaped = value
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    const paragraphs = escaped.split(/\n{2,}/);
    return paragraphs
      .map((p) => `<p>${p.replace(/\n/g, "<br/>")}</p>`)
      .join("");
  }, [value]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Кодовые блоки и т.п. не нужны в задачнике.
        codeBlock: false,
      }),
      Underline,
      Highlight.configure({ multicolor: false }),
      TextStyle,
      FontSize,
      Color,
      BackgroundColor,
      Placeholder.configure({
        placeholder: placeholder ?? "",
        showOnlyWhenEditable: true,
      }),
    ],
    content: initialContent,
    editable: !readOnly,
    autofocus: autoFocus ? "end" : false,
    editorProps: {
      attributes: {
        class: cn(
          "rt-editor min-w-0 px-3 py-2.5 text-[13px] leading-relaxed text-foreground",
          "focus:outline-none",
          "prose prose-sm max-w-none",
          // Tailwind prose цвета подменяем на наши.
          "prose-headings:text-foreground prose-p:text-foreground prose-li:text-foreground",
          "prose-strong:text-foreground prose-em:text-foreground",
          "prose-a:text-[var(--nn-blue)] prose-a:underline-offset-2",
          "prose-h1:text-[18px] prose-h1:font-bold prose-h1:mt-1 prose-h1:mb-1",
          "prose-h2:text-[15px] prose-h2:font-bold prose-h2:mt-1 prose-h2:mb-1",
          "prose-h3:text-[13px] prose-h3:font-bold prose-h3:mt-1 prose-h3:mb-0.5",
          "prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0",
          "prose-mark:bg-yellow-200/80 dark:prose-mark:bg-yellow-200/40",
        ),
        style: `min-height: ${minHeight}`,
        "data-placeholder": placeholder ?? "",
      },
      handleKeyDown: (_view, event) => {
        if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
          if (onSubmitRef.current) {
            event.preventDefault();
            onSubmitRef.current();
            return true;
          }
        }
        return false;
      },
    },
    onUpdate: ({ editor: ed }) => {
      onChangeRef.current(htmlOrEmpty(ed));
    },
  });

  // Внешнее обновление value (например, после очистки черновика) — синхронизируем.
  useEffect(() => {
    if (!editor) return;
    const cur = htmlOrEmpty(editor);
    const next = looksLikeHtml(value) || !value ? value : initialContent;
    if (cur === next) return;
    // setContent с emitUpdate=false — чтобы не зациклить onUpdate.
    editor.commands.setContent(next || "", { emitUpdate: false });
  }, [value, editor, initialContent]);

  useEffect(() => {
    if (!editor) return;
    if (editor.isEditable === !readOnly) return;
    editor.setEditable(!readOnly);
  }, [editor, readOnly]);

  if (!editor) {
    return (
      <div
        className={cn("rounded-md border border-border/80 bg-background", className)}
        style={{ minHeight }}
      />
    );
  }

  return (
    <div
      className={cn(
        // НЕ ставим overflow-hidden: иначе sticky-тулбар внутри потеряет
        // свой scroll-контекст и не «прилипнет» к верху при скролле родителя.
        "rt-editor-shell rounded-md border border-border/80 bg-background transition-colors",
        "focus-within:border-[var(--nn-blue)] focus-within:ring-2 focus-within:ring-[var(--nn-blue)]/20",
        readOnly && "opacity-95",
        className,
      )}
      onPasteCapture={onPasteCapture}
      onDropCapture={onDropCapture}
    >
      {!readOnly ? <RichTextToolbar editor={editor} /> : null}
      <EditorContent editor={editor} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Read-only renderer

interface RichTextViewProps {
  html: string;
  className?: string;
}

/**
 * Безопасно отрендерить HTML для просмотра (используем тот же набор стилей,
 * что и редактор). Для plain-text оборачиваем в whitespace-pre-wrap.
 */
export function RichTextView({ html, className }: RichTextViewProps) {
  if (!html) return null;
  if (!looksLikeHtml(html)) {
    return (
      <div
        className={cn("whitespace-pre-wrap break-words text-foreground/90", className)}
      >
        {html}
      </div>
    );
  }
  return (
    <div
      className={cn(
        "rt-view prose prose-sm max-w-none break-words",
        "prose-headings:text-foreground prose-p:text-foreground prose-li:text-foreground",
        "prose-strong:text-foreground prose-em:text-foreground",
        "prose-a:text-[var(--nn-blue)] prose-a:underline-offset-2",
        "prose-h1:text-[18px] prose-h1:font-bold prose-h1:mt-1 prose-h1:mb-1",
        "prose-h2:text-[15px] prose-h2:font-bold prose-h2:mt-1 prose-h2:mb-1",
        "prose-h3:text-[13px] prose-h3:font-bold prose-h3:mt-1 prose-h3:mb-0.5",
        "prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0",
        "prose-mark:bg-yellow-200/80 dark:prose-mark:bg-yellow-200/40",
        className,
      )}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
